import { Modal } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';

import useApi from '../utils/AxiosWrapper';
import { unwrapApiResponse } from '../utils/unwrapApiResponse';
import { invalidateCatenaMileageQueries } from '../components/dashboard/customers/tabs/assets/catenaMileage';

export type CatenaConnectionStatus =
  | 'invited'
  | 'viewed'
  | 'accepted'
  | 'connected'
  | 'declined'
  | 'expired'
  | 'staled'
  | 'disconnected';

export interface CatenaConnection {
  connection_id: string;
  status: CatenaConnectionStatus;
  connected_tsps: string[] | null;
  last_synced_at: string | null;
  magic_link: string | null;
  invited_at: string | null;
}

export type CatenaCardState =
  | 'not_connected'
  | 'connecting'
  | 'connected'
  | 'needs_reconnect';

// Format a TSP slug for display, e.g. 'motive' -> 'Motive', 'keep_truckin' -> 'Keep Truckin'.
export function formatTspName(slug: string): string {
  return slug
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Each poll tick reconciles from Catena (2 API calls). The old flat 5s x 15 gave up after 75
// seconds — far short of the real wait, since the customer is away signing in to their TSP and
// Catena's roster lands 3-7 minutes after that. Cover the real window instead, but back off hard:
// the card now shows a resume action, so polling only has to catch a completion that happens
// elsewhere. ~9.5 minutes in 23 ticks, vs 15 ticks in 75 seconds.
const POLL_SCHEDULE_MS = [
  ...Array(3).fill(5000),
  ...Array(4).fill(15000),
  ...Array(16).fill(30000),
];

// Statuses where the link is started but not finished — the customer still has a step to take.
const IN_FLIGHT_STATUSES: CatenaConnectionStatus[] = [
  'invited',
  'viewed',
  'accepted',
];

function isInFlight(status?: CatenaConnectionStatus): boolean {
  return !!status && IN_FLIGHT_STATUSES.includes(status);
}

// A customer can accumulate multiple connection rows (each Connect attempt creates one). Pick the
// row that reflects reality, not just the newest: a live `connected` row must win over a newer,
// half-finished `invited` row, otherwise a stale invite shadows an active connection and the card
// is stuck on "Finishing setup" forever. Ties fall to the most recent (the list is created_at DESC).
const STATUS_PRIORITY: Record<string, number> = {
  connected: 4,
  staled: 3,
  accepted: 2,
  viewed: 2,
  invited: 2,
  declined: 1,
  expired: 1,
  disconnected: 0,
};

function selectCurrentConnection(
  connections: CatenaConnection[]
): CatenaConnection | null {
  if (!connections.length) return null;
  return connections.reduce((best, c) =>
    (STATUS_PRIORITY[c.status] ?? 0) > (STATUS_PRIORITY[best.status] ?? 0)
      ? c
      : best
  );
}

export function statusToState(
  status?: CatenaConnectionStatus
): CatenaCardState {
  switch (status) {
    case 'invited':
    case 'viewed':
    case 'accepted':
      return 'connecting';
    case 'connected':
      return 'connected';
    case 'staled':
      return 'needs_reconnect';
    default:
      return 'not_connected';
  }
}

export interface UseCatenaConnectionResult {
  state: CatenaCardState;
  connectedTsps: string[];
  loading: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  refresh: () => Promise<void>;
  sync: () => Promise<void>;
}

export function useCatenaConnection(
  customerId: string | undefined,
  enabled: boolean,
  // Read-only consumers (the dashboard onboarding row) opt out of the live lifecycle: they render
  // a cached snapshot and route to the settings card to act. Skipping BOTH the mount reconcile and
  // the poll keeps two Catena API calls off the dashboard's critical path, where this hook already
  // sits behind a user-context -> feature-flag -> connections waterfall.
  live: boolean = true
): UseCatenaConnectionResult {
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  const [connection, setConnection] = useState<CatenaConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCountRef = useRef(0);
  const notifiedNativeRef = useRef(false);
  const mountedRef = useRef(true);
  const loadRunRef = useRef(0);

  const state = statusToState(connection?.status);

  // Both readers unwrap rather than optional-chaining into `data`. AxiosWrapper never rejects — it
  // resolves with the error response — so `response?.data?.data?.connections ?? []` renders a 500
  // as "this customer has no connection". That single conflation is what let one flaky /sync blank
  // the card AND permanently stop the poll (it read null as "settled"). Throwing keeps "Catena says
  // no connection" and "we could not reach Catena" apart, which every caller below already assumes:
  // each has a catch that was, until now, unreachable.
  const fetchConnection =
    useCallback(async (): Promise<CatenaConnection | null> => {
      if (!customerId) return null;
      const response = await callProtectedApi(
        '/electronicLoggingDevices/catena/connections/' + customerId,
        'get'
      );
      const { connections = [] } = unwrapApiResponse<{
        connections?: CatenaConnection[];
      }>(response, 'Could not load your ELD connection.');
      return selectCurrentConnection(connections);
    }, [callProtectedApi, customerId]);

  // Reconcile from Catena (POST /sync) and return the resulting current connection. Unlike
  // fetchConnection (a plain read of our DB), this advances a row stuck in "connecting" when the
  // completion webhook never landed — so polling actually progresses instead of re-reading a
  // stale row.
  const reconcile = useCallback(async (): Promise<CatenaConnection | null> => {
    if (!customerId) return null;
    const response = await callProtectedApi(
      '/electronicLoggingDevices/catena/connections/' + customerId + '/sync',
      'post',
      {},
      'Could not sync your ELD connection. Please try again.'
    );
    const { connections = [] } = unwrapApiResponse<{
      connections?: CatenaConnection[];
    }>(response, 'Could not sync your ELD connection.');
    return selectCurrentConnection(connections);
  }, [callProtectedApi, customerId]);

  // Bumped by stopPolling. clearTimeout cannot cancel a callback that has ALREADY fired and is
  // parked on `await reconcile()` — that tick would resolve after unmount/disconnect, write stale
  // state, and re-arm a chain nothing holds a handle to. Each tick captures the generation it was
  // scheduled in and discards itself if that number moved.
  const pollGenerationRef = useRef(0);

  const stopPolling = useCallback(() => {
    pollGenerationRef.current += 1;
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const maybeNotifyNative = useCallback((conn: CatenaConnection | null) => {
    if (
      conn?.status === 'connected' &&
      !notifiedNativeRef.current &&
      (window as any).ReactNativeWebView
    ) {
      notifiedNativeRef.current = true;
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'ELD_LINKED' })
      );
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const conn = await fetchConnection();
      setConnection(conn);
      maybeNotifyNative(conn);
    } catch (e) {
      console.error('Error fetching Catena connection:', e);
    }
  }, [fetchConnection, maybeNotifyNative]);

  // Chained timeouts rather than a fixed interval, so each tick can use its own delay from
  // POLL_SCHEDULE_MS and the schedule can back off as the wait gets longer.
  const startPolling = useCallback(() => {
    stopPolling();
    const generation = pollGenerationRef.current;
    pollCountRef.current = 0;
    const isCurrent = () => generation === pollGenerationRef.current;

    const scheduleNext = () => {
      if (!isCurrent()) return;
      const delay = POLL_SCHEDULE_MS[pollCountRef.current];
      // Schedule exhausted: stop. The card already shows the customer their next step, so
      // there is nothing left to wait for on a timer.
      if (delay === undefined) return;
      pollRef.current = setTimeout(async () => {
        pollCountRef.current += 1;
        try {
          const conn = await reconcile();
          // Polling stopped while this tick was in flight (unmount, disconnect, manual sync) —
          // drop the result rather than overwriting whatever replaced it.
          if (!isCurrent()) return;
          setConnection(conn);
          maybeNotifyNative(conn);
          if (!isInFlight(conn?.status)) {
            // Settled (connected, stale, or terminal) — stop without rescheduling. Mileage submit
            // surfaces cache catena_connected (THT-678); refetch so they disable/enable as soon as
            // the connection state settles.
            invalidateCatenaMileageQueries(queryClient);
            return;
          }
        } catch (e) {
          // A failed /sync is NOT "settled" — it tells us nothing. Log and let the schedule carry
          // on, so one transient 5xx can't strand the customer on "Finishing setup" until reload.
          console.error('Error polling Catena connection:', e);
        }
        scheduleNext();
      }, delay);
    };

    scheduleNext();
  }, [reconcile, maybeNotifyNative, stopPolling, queryClient]);

  // Load the current connection and, if the link is still mid-flight, start polling. Shared by
  // mount and by the bfcache restore below, so both paths recover identically.
  //
  // Deliberately NOT memoized. useApi() builds a new callProtectedApi every render, so
  // fetchConnection / reconcile / startPolling all change identity every render — a useCallback
  // here would freeze whichever copies existed when its deps last changed, and the pageshow
  // listener below would call that frozen copy forever. callProtectedApi captures isAuthenticated
  // and throws when it is false, so a copy captured while Auth0 was mid re-check throws on every
  // later bfcache restore, gets swallowed by the catch, and leaves the card exactly as stale as it
  // was before the customer left — the dead end pageshow exists to fix. The effects invoke through
  // loadAndPollRef so they never hold a version of their own.
  const loadAndPoll = async () => {
    if (!customerId) return;
    // Per-run token: a load is only allowed to write state if it is still the newest one AND the
    // component is mounted. The effect's cleanup bumps this, so a superseded run resolves into a
    // no-op instead of racing the run that replaced it.
    const run = ++loadRunRef.current;
    const isCurrentRun = () => run === loadRunRef.current && mountedRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const justReturned =
        params.has('invitation_id') || params.has('fleet_ref');
      if (justReturned) {
        toast.success('Finishing setup…');
        params.delete('invitation_id');
        params.delete('fleet_ref');
        const qs = params.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (qs ? `?${qs}` : '')
        );
      }

      // A plain DB read can't tell "still connecting" from "stalled, waiting on them" — our row
      // only moves when a webhook lands. So reconcile (ask Catena directly) whenever we just
      // came back from the hosted flow OR the row is mid-flight, and only then render a state
      // that tells the customer to act. This is what makes the resume prompt trustworthy
      // ~1-2 seconds in, instead of a 75-second timer that guessed.
      // Always read the row first, even on the return leg. It costs one GET and buys two things:
      // a fallback when the reconcile below fails, and a correct answer for `live: false` surfaces
      // (the dashboard row), which otherwise consumed the return params and rendered nothing.
      const cached = await fetchConnection();

      let conn = cached;
      let reconciled = false;
      if (live && (justReturned || isInFlight(cached?.status))) {
        try {
          conn = await reconcile();
          reconciled = true;
        } catch (e) {
          // A sync outage must not blank the card. Falling back to the row keeps "Finish
          // connecting" on screen; rendering "not connected" would invite a fresh Connect that
          // Catena rejects while their consent is live.
          console.error('Error reconciling Catena connection:', e);
        }
      }
      if (!isCurrentRun()) return;
      setConnection(conn);
      maybeNotifyNative(conn);

      if (live && (justReturned || isInFlight(conn?.status))) {
        startPolling();
      } else if (reconciled) {
        // Settled on the mount path, so no poll tick will ever run to do this. Mileage submit
        // reads a 15-minute cached catena_connected (THT-678) and would keep offering manual entry
        // the backend now rejects.
        invalidateCatenaMileageQueries(queryClient);
      }
    } catch (e) {
      console.error('Error loading Catena connection:', e);
    } finally {
      if (isCurrentRun()) setLoading(false);
    }
  };

  const loadAndPollRef = useRef(loadAndPoll);
  loadAndPollRef.current = loadAndPoll;

  useEffect(() => {
    mountedRef.current = true;
    if (enabled && customerId) void loadAndPollRef.current();
    return () => {
      mountedRef.current = false;
      // Invalidate any load still in flight. A shared mounted flag can't do this on its own: the
      // NEXT effect run sets it back to true before the stale load checks it, so a logout (or the
      // feature flag flipping off) would let the previous session's row land in state and start a
      // poll — defeating the kill switch IntegrationSettings documents.
      loadRunRef.current += 1;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, customerId, live]);

  // `connect()` leaves the app entirely (`window.location.href = magicLink`). Coming back with the
  // browser's Back button restores this page from the bfcache: the component is NOT remounted, so
  // the effect above never re-runs — and `loading` is still true from the click that navigated
  // away. The result is a permanently disabled spinner until the customer manually reloads, which
  // is the dead end this whole change exists to remove. `pageshow` with `persisted` is the only
  // event that fires for a bfcache restore, so re-run the load there.
  useEffect(() => {
    if (!enabled || !customerId) return;
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) void loadAndPollRef.current();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [enabled, customerId]);

  const requestInvitation = useCallback(async () => {
    return callProtectedApi(
      '/electronicLoggingDevices/catena/invitations/' + customerId,
      'post',
      {},
      'Could not start ELD connection. Please try again.'
    );
  }, [callProtectedApi, customerId]);

  const requestDisconnect = useCallback(
    async (errorMsg = 'Could not disconnect your ELD. Please try again.') => {
      return callProtectedApi(
        '/electronicLoggingDevices/catena/connections/' + customerId,
        'delete',
        {},
        errorMsg
      );
    },
    [callProtectedApi, customerId]
  );

  // Follows the invitation's magic link, or surfaces a failure and clears loading.
  const followInvitation = useCallback(
    (response: Awaited<ReturnType<typeof requestInvitation>>) => {
      const magicLink: string | undefined = response?.data?.data?.magic_link;
      if (magicLink) {
        window.location.href = magicLink;
      } else {
        toast.error('Could not start ELD connection. Please try again.');
        setLoading(false);
      }
    },
    []
  );

  const connect = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const response = await requestInvitation();

      // The fleet can still be linked at Catena from a previous attempt that stalled or whose
      // local record was lost — the backend returns 409 { reason: 'already_linked' } (which
      // AxiosWrapper intentionally does not toast). Offer to cancel that stale link and retry, so
      // the user can self-recover instead of being permanently blocked.
      //
      // Gated on `not_connected` specifically, NOT on "anything but connected". A `connecting` row
      // means the customer already consented at Catena, so the live agreement this modal offers to
      // cancel IS their progress — one confirm and the setup they were a step away from finishing
      // is gone. `needs_reconnect` (staled) is excluded for the same reason: the fleet did connect.
      // arch #1550 makes this 409 rare, but "the backend rarely sends it" is not a safe reason to
      // leave a destructive action reachable from a state where it is always the wrong answer.
      if (
        state === 'not_connected' &&
        response?.status === 409 &&
        response?.data?.data?.reason === 'already_linked'
      ) {
        Modal.confirm({
          title: 'Reset previous ELD connection?',
          content:
            'It looks like this ELD is still linked from a previous attempt. Reset that connection and start over?',
          okText: 'Reset & retry',
          okType: 'danger',
          cancelText: 'Cancel',
          async onOk() {
            await requestDisconnect(
              'Could not reset your previous ELD connection. Please try again.'
            );
            notifiedNativeRef.current = false;
            followInvitation(await requestInvitation());
          },
          onCancel() {
            setLoading(false);
          },
        });
        return;
      }

      followInvitation(response);
    } catch (e) {
      console.error('Error creating Catena invitation:', e);
      setLoading(false);
    }
  }, [
    requestInvitation,
    requestDisconnect,
    followInvitation,
    customerId,
    state,
  ]);

  const disconnect = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      await requestDisconnect();
      stopPolling();
      notifiedNativeRef.current = false; // allow native notify again if they reconnect later
      await refresh();
      // Mileage submit surfaces cache catena_connected (THT-678); without this
      // a disconnected customer stays locked out of manual entry until the
      // 15-minute staleTime lapses.
      invalidateCatenaMileageQueries(queryClient);
    } catch (e) {
      console.error('Error disconnecting Catena connection:', e);
    } finally {
      setLoading(false);
    }
  }, [requestDisconnect, customerId, refresh, stopPolling, queryClient]);

  // Pull-based reconcile: ask the backend to sync our state with Catena's truth, then adopt the
  // returned row. Recovers a connection stuck in "Finishing setup" (a webhook never landed) or
  // one whose local record was deleted while Catena still holds the link.
  const sync = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const conn = await reconcile();
      setConnection(conn);
      maybeNotifyNative(conn);
      if (isInFlight(conn?.status)) {
        // A manual sync that lands on a still-connecting row must leave a timer running, or this
        // becomes a one-shot: the customer syncs, the row is mid-flight, and nothing ever checks
        // again — the completion webhook lands minutes later against a card that never updates.
        startPolling();
      } else {
        stopPolling();
        invalidateCatenaMileageQueries(queryClient);
      }
    } catch (e) {
      console.error('Error syncing Catena connection:', e);
    } finally {
      setLoading(false);
    }
  }, [
    reconcile,
    customerId,
    maybeNotifyNative,
    startPolling,
    stopPolling,
    queryClient,
  ]);

  return {
    state,
    connectedTsps: connection?.connected_tsps ?? [],
    loading,
    connect,
    disconnect,
    refresh,
    sync,
  };
}
